import React, { useState, useEffect } from 'react';
import { X, Trash2, AlertTriangle, Package, Calendar, Clock, User } from 'lucide-react';

const DeleteEventModal = ({ 
  isOpen = false, 
  onClose, 
  onConfirm, 
  event = null,
  isLoading = false 
}) => {
  const [manifestInput, setManifestInput] = useState('');
  const [isConfirmEnabled, setIsConfirmEnabled] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setManifestInput('');
      setIsConfirmEnabled(false);
    }
  }, [isOpen]);

  // Check if manifest input matches the event's manifest
  useEffect(() => {
    if (event && manifestInput.trim() === event.shipment?.manifest) {
      setIsConfirmEnabled(true);
    } else {
      setIsConfirmEnabled(false);
    }
  }, [manifestInput, event]);

  if (!isOpen || !event) return null;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleConfirm = () => {
    if (isConfirmEnabled && !isLoading) {
      onConfirm(event.id);
    }
  };

  const handleInputChange = (e) => {
    setManifestInput(e.target.value);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-red-50">
          <div className="flex items-center">
            <div className="mr-3 flex-shrink-0 text-red-600">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Eliminar Evento
              </h3>
              <p className="text-sm text-gray-600">
                Esta acción no se puede deshacer
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:cursor-not-allowed"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Warning Message */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-red-800 mb-2">
                  ¡Atención! Esta acción es irreversible
                </h4>
                <p className="text-sm text-red-700">
                  Está a punto de eliminar permanentemente este evento. Una vez eliminado, 
                  no podrá recuperar la información ni deshacer esta acción.
                </p>
              </div>
            </div>
          </div>

          {/* Event Information */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Trash2 className="w-5 h-5 mr-2 text-red-600" />
              Información del Evento a Eliminar
            </h4>
            
            <div className="space-y-4">
              {/* Event Details */}
              <div className="border-l-4 border-red-500 pl-4">
                <h5 className="font-medium text-gray-900 mb-2">Detalles del Estado</h5>
                <p className="text-gray-700 text-sm bg-white p-3 rounded border">
                  {event.status_detail}
                </p>
              </div>

              {/* Event Metadata */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center space-x-3">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <div>
                    <p className="text-xs text-gray-500">Fecha del Estado</p>
                    <p className="text-sm font-medium text-gray-900">
                      {formatDate(event.status_date)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Clock className="h-4 w-4 text-gray-500" />
                  <div>
                    <p className="text-xs text-gray-500">Creado</p>
                    <p className="text-sm font-medium text-gray-900">
                      {formatDate(event.created_at)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Shipment Information */}
              <div className="border-t border-gray-200 pt-4">
                <h5 className="font-medium text-gray-900 mb-3">Información del Manifiesto</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3">
                    <Package className="h-4 w-4 text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">Manifiesto</p>
                      <p className="text-sm font-medium text-gray-900">
                        {event.shipment.manifest}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <User className="h-4 w-4 text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">Responsable</p>
                      <p className="text-sm font-medium text-gray-900">
                        {event.shipment.loggedUser?.full_name || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Route */}
                <div className="mt-3">
                  <p className="text-xs text-gray-500">Ruta</p>
                  <p className="text-sm font-medium text-gray-900">
                    {event.shipment.origin?.origin_name || 'N/A'} → {event.shipment.destination?.destination_name || 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Confirmation Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-900 mb-3">
              Para confirmar la eliminación, escriba el manifiesto: 
              <span className="font-bold text-red-600 ml-1">
                {event.shipment.manifest}
              </span>
            </label>
            <input
              type="text"
              value={manifestInput}
              onChange={handleInputChange}
              placeholder={`Escriba: ${event.shipment.manifest}`}
              disabled={isLoading}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              autoComplete="off"
            />
            <div className="mt-2 flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${isConfirmEnabled ? 'bg-green-500' : 'bg-gray-300'}`}></div>
              <p className={`text-xs ${isConfirmEnabled ? 'text-green-600' : 'text-gray-500'}`}>
                {isConfirmEnabled ? 'Confirmación válida' : 'El manifiesto no coincide'}
              </p>
            </div>
          </div>

          {/* Final Warning */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-xs text-yellow-800">
              <strong>Recordatorio:</strong> Solo se pueden eliminar eventos que no han sido aprobados. 
              Una vez eliminado, este evento desaparecerá permanentemente del historial del manifiesto.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={!isConfirmEnabled || isLoading}
            className="inline-flex items-center px-6 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Eliminando...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4 mr-2" />
                Eliminar Evento
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteEventModal;